"use client";

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { TransitionLink } from "@/components/transition-link";
import BgCardsDots from "~/public/assets/services/bg-cards-dots.png";
import BgCardsDotsHover from "~/public/assets/services/bg-cards-dots-hover.png";
import type { ServiceItem } from "./services-content";
gsap.registerPlugin(useGSAP);
export type ServiceDesktopCardProps = {
  service: ServiceItem;
  isActive: boolean;
  onMouseEnter: () => void;
  defaultLinkLabel: string;
  defaultLinkHref: string;
};
export function ServiceDesktopCard(t0) {
  const $ = _c(31);
  const {
    service,
    isActive,
    onMouseEnter,
    defaultLinkLabel,
    defaultLinkHref
  } = t0;
  const cardRef = useRef(null);
  const isFirstRun = useRef(true);
  let t1;
  let t2;
  if ($[0] !== isActive) {
    t1 = () => {
      const instant = isFirstRun.current;
      isFirstRun.current = false;
      const tween = (target, vars, duration, t3) => {
        const delay = t3 === undefined ? 0 : t3;
        if (instant) {
          gsap.set(target, vars);
          return;
        }
        gsap.to(target, {
          ...vars,
          duration,
          delay,
          ease: "power2.out",
          overwrite: "auto"
        });
      };
      tween("[data-anim='dots']", {
        opacity: isActive ? 0 : 1
      }, 0.2);
      tween("[data-anim='dots-hover']", {
        opacity: isActive ? 1 : 0
      }, 0.2);
      tween("[data-anim='glow']", {
        opacity: isActive ? 1 : 0
      }, 0.4);
      tween("[data-anim='arrow']", {
        opacity: isActive ? 1 : 0,
        x: isActive ? 0 : -6,
        y: isActive ? 0 : 16
      }, 0.22);
      tween("[data-anim='icon']", {
        y: isActive ? 2 : 52
      }, 0.44);
      tween("[data-anim='icon-fade']", {
        opacity: isActive ? 0 : 1
      }, 0.4);
      tween("[data-anim='title']", {
        color: isActive ? "#ff5c00" : "#ffffff",
        y: isActive ? 0 : 52
      }, 0.44);
      tween("[data-anim='description']", {
        opacity: isActive ? 1 : 0,
        y: isActive ? 0 : 8
      }, 0.42, isActive ? 0.04 : 0);
    };
    t2 = {
      dependencies: [isActive],
      scope: cardRef
    };
    $[0] = isActive;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useGSAP(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="pointer-events-none absolute top-2 right-2 h-full max-h-86 w-full max-w-88" data-anim="dots"><Image src={BgCardsDots} alt="" aria-hidden="true" className="h-full max-h-86 w-full max-w-88" width={2000} height={2000} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute top-2 right-2 h-full max-h-86 w-full max-w-88 opacity-0" data-anim="dots-hover"><Image src={BgCardsDotsHover} alt="" aria-hidden="true" className="h-full max-h-86 w-full max-w-88" width={2000} height={2000} /></div>;
    t5 = <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_7%,rgba(255,92,0,0.085),rgba(255,92,0,0.028)_28%,rgba(0,0,0,0)_60%)] opacity-0" data-anim="glow" />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="absolute top-2 right-4 z-20 text-primary-300 opacity-0" data-anim="arrow"><ArrowUpRight size={32} strokeWidth={1.5} /></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== service.title) {
    t7 = service.title.replace("\n", " ");
    $[7] = service.title;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = `${defaultLinkLabel}: ${t7}`;
  const t9 = service.linkHref ?? defaultLinkHref;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      transform: "translateY(52px)"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== service.icon) {
    t11 = <Image src={service.icon} alt="" aria-hidden="true" className="w-full max-w-35" height={1060} width={1060} />;
    $[10] = service.icon;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-linear-to-t from-black/80 via-black/84 to-transparent" data-anim="icon-fade" />;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t11) {
    t13 = <div className="relative h-full max-h-40.25" data-anim="icon" style={t10}>{t11}{t12}</div>;
    $[13] = t11;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      transform: "translateY(52px)"
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== service.title) {
    t15 = <h3 className="mt-4 whitespace-pre-line text-[26px] leading-[130%] text-white" data-anim="title" style={t14}>{service.title}</h3>;
    $[16] = service.title;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const t16 = !isActive;
  const t17 = isActive ? "mt-6 min-h-21 text-[15px] leading-[140%] text-white/60 whitespace-pre-line" : "pointer-events-none mt-6 min-h-21 text-[15px] leading-[140%] text-white/60 opacity-0 whitespace-pre-line";
  let t18;
  if ($[18] !== service.description || $[19] !== t16 || $[20] !== t17) {
    t18 = <p aria-hidden={t16} className={t17} data-anim="description">{service.description}</p>;
    $[18] = service.description;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== t13 || $[23] !== t15 || $[24] !== t18 || $[25] !== t8 || $[26] !== t9) {
    t19 = <TransitionLink aria-label={t8} className="relative z-10 flex h-full flex-col items-center px-8 2xl:px-12! justify-center text-center outline-none" href={t9}>{t13}{t15}{t18}</TransitionLink>;
    $[22] = t13;
    $[23] = t15;
    $[24] = t18;
    $[25] = t8;
    $[26] = t9;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== onMouseEnter || $[29] !== t19) {
    t20 = <article className="relative h-112 w-full flex-1 overflow-hidden rounded-2xl bg-black" onFocus={onMouseEnter} onMouseEnter={onMouseEnter} ref={cardRef}>{t3}{t4}{t5}{t6}{t19}</article>;
    $[28] = onMouseEnter;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  return t20;
}